import React, { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LifeBuoy } from "lucide-react";
import { AccountListManager } from "@/components/admin/AccountListManager";

const AUTOREPLY_LANGS = [["fr", "Français"], ["en", "English"], ["es", "Español"], ["pt", "Português"]];
const EMPTY_AUTOREPLY_CFG = { texts: {}, wa_phone: "", wa_messages: {} };

// Lot 591 — réglages de la réponse automatique : texte par langue (vide = texte par défaut) + WhatsApp support optionnel.
const SupportAutoReplyConfig = ({ cfg, setCfg, defaults, isSuper }) => {
  const [lang, setLang] = useState("fr");
  const d = defaults?.[lang] || {};
  const setLangField = (field, value) => setCfg((c) => ({ ...c, [field]: { ...(c[field] || {}), [lang]: value } }));
  const customCount = AUTOREPLY_LANGS.filter(([l]) => (cfg.texts?.[l] || "").trim()).length;
  return (
    <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: "var(--yalola-border)" }} data-testid="support-autoreply-config">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-semibold text-sm">Texte de la réponse</div>
        <div className="flex gap-1" data-testid="support-autoreply-langs">
          {AUTOREPLY_LANGS.map(([l, label]) => (
            <button key={l} type="button" onClick={() => setLang(l)} title={label} data-testid={`support-autoreply-lang-${l}`} data-active={lang === l ? 1 : 0}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase transition-colors ${lang === l ? "text-white" : "opacity-60 hover:opacity-100"}`}
              style={lang === l ? { background: "var(--yalola-primary)" } : { background: "rgba(0,0,0,0.05)" }}>
              {l}{(cfg.texts?.[l] || "").trim() ? " •" : ""}
            </button>
          ))}
        </div>
      </div>
      <Textarea rows={4} value={cfg.texts?.[lang] || ""} onChange={(e) => setLangField("texts", e.target.value)} disabled={!isSuper} maxLength={600}
        placeholder={d.text || "Texte par défaut"} data-testid={`support-autoreply-text-${lang}`} />
      <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="support-autoreply-text-hint">
        Vide = texte par défaut dans cette langue (affiché en gris). <code>{"{prenom}"}</code> est remplacé par le prénom du membre.
        {customCount > 0 && <> · <b>{customCount}</b> langue(s) personnalisée(s).</>}
      </p>
      <div className="font-semibold text-sm pt-1">Lien WhatsApp support (optionnel)</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Numéro WhatsApp (format international)</Label>
          <Input value={cfg.wa_phone || ""} onChange={(e) => setCfg((c) => ({ ...c, wa_phone: e.target.value }))} disabled={!isSuper} placeholder="+243 812 345 678" inputMode="tel" data-testid="support-autoreply-wa-phone" />
        </div>
        <div>
          <Label className="text-xs">Message pré-rempli ({AUTOREPLY_LANGS.find(([l]) => l === lang)?.[1]})</Label>
          <Input value={cfg.wa_messages?.[lang] || ""} onChange={(e) => setLangField("wa_messages", e.target.value)} disabled={!isSuper} maxLength={300}
            placeholder={d.wa_message || ""} data-testid={`support-autoreply-wa-msg-${lang}`} />
        </div>
      </div>
      <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="support-autoreply-wa-hint">
        {(cfg.wa_phone || "").trim()
          ? <>Un 2e bouton <b>« Écrire sur WhatsApp »</b> s'affiche sous « Ouvrir Aide & support » et ouvre WhatsApp avec ce message (dans la langue du membre).</>
          : <>Sans numéro, seul le bouton « Ouvrir Aide & support » est affiché.</>}
      </p>
    </div>
  );
};

// Lot 590 — un membre écrit en privé à l'un de ces comptes officiels → réponse automatique avec le bouton « Ouvrir Aide & support ».
// Lot 591 — texte modifiable par langue + bouton WhatsApp support optionnel (une seule sauvegarde pour la liste et les réglages).
export const SupportAutoReplyManager = ({ isSuper }) => {
  const [cfg, setCfg] = useState(EMPTY_AUTOREPLY_CFG);
  const [defaults, setDefaults] = useState({});
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;
  return (
    <AccountListManager isSuper={isSuper} endpoint="/admin/support-autoreply" title="Réponse automatique « Aide & support »" icon={LifeBuoy} testPrefix="support-autoreply"
      successMsg="Réponse automatique mise à jour ✅"
      onLoaded={(d) => { setCfg({ ...EMPTY_AUTOREPLY_CFG, ...(d.config || {}) }); setDefaults(d.defaults || {}); }}
      extraPayload={() => ({ config: cfgRef.current })}
      description={<>Quand un membre écrit en privé à l'un de ces comptes (ex. <b>Yalola App</b>), il reçoit aussitôt un message automatique dans sa langue avec un bouton <b>« Ouvrir Aide & support »</b> (web et mobile) — une fois par conversation et par 24 h.</>}>
      <SupportAutoReplyConfig cfg={cfg} setCfg={setCfg} defaults={defaults} isSuper={isSuper} />
    </AccountListManager>
  );
};
